import axios from 'axios';
import type { AxiosError, AxiosInstance } from 'axios';
import type { ApiError } from '../types';
import { notify } from '../components/Notifier';
import { API_REQUEST_TIMEOUT, API_UPLOAD_TIMEOUT } from '../constants';

/**
 * Axios client dùng chung.
 *  - baseURL '/api' (Vite proxy → Worker khi dev).
 *  - withCredentials: gửi cookie HttpOnly JWT.
 *  - Interceptor: 401 → về /login; 403 → snackbar cảnh báo quyền.
 */
/**
 * baseURL:
 *  - Dev: '/api' (Vite proxy → Worker localhost:8787).
 *  - Prod (web & api khác domain): đặt VITE_API_BASE khi build, VD
 *    "https://webqn-api.<subdomain>.workers.dev/api".
 */
export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: API_REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const uploadApi: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: API_UPLOAD_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Với FormData (upload ảnh), phải để axios tự đặt
 * `Content-Type: multipart/form-data; boundary=...`. Nếu giữ header mặc định
 * `application/json`, request thiếu boundary và Worker sẽ từ chối
 * (`c.req.formData()` throw). Vì vậy xóa Content-Type để trình duyệt/axios
 * tự sinh header đúng.
 */
api.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const headers = config.headers;
    if (headers) {
      // AxiosHeaders (axios v1) có .delete() chuẩn hóa key; fallback cho object thường.
      if (typeof (headers as { delete?: unknown }).delete === 'function') {
        (headers as unknown as { delete: (name: string) => void }).delete('Content-Type');
      } else {
        delete (headers as Record<string, unknown>)['Content-Type'];
        delete (headers as Record<string, unknown>)['content-type'];
      }
    }
  }
  return config;
});

// Các path tự xử lý lỗi 401/403 tại chỗ; interceptor KHÔNG được đá về /login,
// nếu không thông báo lỗi (chờ duyệt / bị khóa / sai domain) sẽ bị nuốt mất.
/** Các endpoint mà 401 là trạng thái BÌNH THƯỜNG (không redirect). */
const AUTH_PROBE_PATHS = ['/auth/me', '/auth/login', '/auth/google', '/auth/google/config'];

function isAuthProbe(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_PROBE_PATHS.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url;

    if (status === 401 && !isAuthProbe(requestUrl)) {
      // Phiên hết hạn / chưa đăng nhập cho tài nguyên cần quyền → về trang đăng nhập.
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    } else if (status === 403 && !isAuthProbe(requestUrl)) {
      // Các endpoint auth-probe (vd /auth/google) trả 403 kèm message tiếng Việt
      // riêng do trang gọi tự hiển thị; toast chung ở đây sẽ đè/nhiễu thông báo đó.
      notify('Bạn không đủ quyền thực hiện thao tác này', 'error');
    } else if (!requestUrl?.includes('/auth/')) {
      // Hiển thị feedback cho lỗi server (5xx) hoặc mất kết nối (network error)
      if (status && status >= 500) {
        notify('Máy chủ gặp lỗi. Vui lòng thử lại sau', 'error');
      } else if (!status) {
        // Network error, timeout, hoặc không kết nối được
        notify('Kết nối bị gián đoạn. Kiểm tra internet và thử lại', 'error');
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Dựng URL tuyệt đối để hiển thị ảnh trong thẻ <img>.
 *
 * Backend trả `file_url` dạng tương đối ("/api/images/:id/raw"). Thẻ <img> KHÔNG
 * đi qua axios baseURL nên URL tương đối sẽ phân giải theo origin của trang web
 * (Pages) thay vì Worker (API) → ảnh vỡ khi web & API khác domain. Vì vậy luôn
 * dựng lại theo API_BASE, trừ khi backend đã trả URL tuyệt đối (http/https).
 */
export function resolveImageUrl(img: { id: string; file_url?: string | null }): string {
  const url = img.file_url;
  if (url && /^https?:\/\//i.test(url)) return url;
  return `${API_BASE}/images/${img.id}/raw`;
}

/** Như resolveImageUrl nhưng cho ảnh đính kèm của bug (/api/bug-images/:id/raw). */
export function resolveBugImageUrl(img: { id: string; file_url?: string | null }): string {
  const url = img.file_url;
  if (url && /^https?:\/\//i.test(url)) return url;
  return `${API_BASE}/bug-images/${img.id}/raw`;
}

/** Như resolveImageUrl nhưng cho ảnh của mốc nghiệm thu. */
export function resolveConfirmationImageUrl(img: { id: string; file_url?: string | null }): string {
  const url = img.file_url;
  if (url && /^https?:\/\//i.test(url)) return url;
  return `${API_BASE}/confirmation-images/${img.id}/raw`;
}

/**
 * File đính kèm của yêu cầu chức năng yêu cầu đăng nhập (khác ảnh ở trên,
 * vốn công khai) — không dùng được trực tiếp trong thẻ <img src>/<a href>,
 * phải tải qua axios (có cookie) rồi dựng blob URL. Dùng cùng với
 * featureRequestFileApi ở endpoints.ts.
 */
export async function fetchFeatureRequestFileBlob(id: string): Promise<Blob> {
  const res = await api.get<Blob>(`/feature-request-files/${id}/raw`, { responseType: 'blob' });
  return res.data;
}

/**
 * Dựng URL tuyệt đối cho MỌI path tương đối backend trả (vd logo cổng truy
 * cập: "/api/portals/:id/logo/raw") — cùng lý do với resolveImageUrl: thẻ
 * <img> không đi qua axios baseURL nên phải tự ghép theo API_BASE.
 */
export function resolveApiAssetUrl(relativePath: string | null | undefined): string | null {
  if (!relativePath) return null;
  if (/^https?:\/\//i.test(relativePath)) return relativePath;
  const path = relativePath.startsWith('/api/') ? relativePath.slice(4) : relativePath;
  return `${API_BASE}${path}`;
}

/** Trích message tiếng Việt từ lỗi API (fallback nếu không có). */
export function getApiErrorMessage(error: unknown, fallback = 'Đã có lỗi xảy ra'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined;
    if (data?.message) return data.message;
  }
  return fallback;
}
